<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanPipelineSwitcher from 'dashboard/components-next/Kanban/KanbanPipelineSwitcher.vue';
import {
  filtrosParaQuery,
  queryParaFiltros,
  temFiltroNaQuery,
  filtrosLembrados,
  lembrarFiltros,
} from 'dashboard/components-next/Kanban/helpers/filtrosNaUrl';
import KanbanBoard from 'dashboard/components-next/Kanban/KanbanBoard.vue';
import KanbanFilters from 'dashboard/components-next/Kanban/KanbanFilters.vue';
import KanbanCardDrawer from 'dashboard/components-next/Kanban/KanbanCardDrawer.vue';
import FieldManagerDialog from 'dashboard/components-next/Kanban/FieldManagerDialog.vue';
import PipelineDialog from 'dashboard/components-next/Kanban/PipelineDialog.vue';
import StageDialog from 'dashboard/components-next/Kanban/StageDialog.vue';
import CardCreateDialog from 'dashboard/components-next/Kanban/CardCreateDialog.vue';
import KanbanBulkBar from 'dashboard/components-next/Kanban/KanbanBulkBar.vue';
import KanbanBulkMoveDialog from 'dashboard/components-next/Kanban/KanbanBulkMoveDialog.vue';
import {
  recusaDeOutroAberto,
  motivoDaRecusa,
} from 'dashboard/components-next/Kanban/helpers/recusaDoMover';

const props = defineProps({
  pipelineId: { type: [String, Number], default: null },
  // Set by the /kanban/new route, which the sidebar's last Kanban entry links to.
  openNewPipeline: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const kanban = useKanbanStore();

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const stageDialogRef = ref(null);

// ACESSO-01 (L8b): funis, etapas e campos, juntar cards e as regras em bloco (`pipelines#create`, `stages#*`,
// `cards#merge`): com a chave ACESSO_GRANULAR ligada, o `kanban:manage`; desligada, o administrador, como hoje.
const { decide } = useAcesso();
const canManage = computed(() =>
  decide('kanban:manage', () => {
    const accountId = currentAccountId.value;
    const accountUser = (currentUser.value?.accounts || []).find(
      account => account.id === accountId
    );
    return accountUser?.role === 'administrator';
  })
);

const pipeline = computed(() => kanban.activePipeline);
const showValue = computed(() => Boolean(pipeline.value?.settings?.show_value));
const currency = computed(() => pipeline.value?.settings?.currency || 'BRL');

const filters = computed({
  get: () => kanban.filters,
  set: value => kanban.setFilters(value),
});

// CRM-01h: filtros na URL (o link reproduz o quadro filtrado) e lembrados por pipeline neste
// navegador. A URL vence o lembrado.
const filtrosIniciais = pipelineId =>
  temFiltroNaQuery(route.query)
    ? queryParaFiltros(route.query)
    : filtrosLembrados(currentAccountId.value, pipelineId);

const sincronizarUrl = () => {
  if (props.openNewPipeline || !kanban.activePipelineId) return;
  router.replace({
    name: 'kanban_pipeline',
    params: { pipelineId: kanban.activePipelineId },
    query: filtrosParaQuery(kanban.filters),
  });
};

// Trocar de pipeline muda a URL; o watch de `pipelineId` carrega o quadro e os filtros dele.
// Antes o seletor só mudava a store, e recarregar voltava para o pipeline antigo.
const trocarPipeline = id => {
  if (id === kanban.activePipelineId) return;
  router.push({ name: 'kanban_pipeline', params: { pipelineId: id } });
};

watch(
  () => kanban.filters,
  value => {
    if (!kanban.activePipelineId || props.openNewPipeline) return;
    lembrarFiltros(currentAccountId.value, kanban.activePipelineId, value);
    sincronizarUrl();
  },
  { deep: true }
);

const onEditStage = stage => stageDialogRef.value?.open(stage, kanban.stages);
const onAddStage = () => stageDialogRef.value?.open(null, kanban.stages);

const openCardId = ref(null);

const onOpenCard = async cardId => {
  openCardId.value = cardId;
  try {
    await kanban.fetchCardDetails(cardId);
  } catch (error) {
    openCardId.value = null;
    useAlert(t('KANBAN.DRAWER.LOAD_FAILED'));
  }
};

const onCloseCard = () => {
  openCardId.value = null;
  kanban.closeDetails();
};

// RN-02: o contato do negócio já tem outro aberto no funil. O card já voltou para onde estava, então o
// quadro não relê; o painel do negócio abre com o motivo e, para o administrador, o "Juntar".
const onDrop = async payload => {
  try {
    await kanban.moveCard(payload);
  } catch (error) {
    const recusa = recusaDeOutroAberto(error);
    if (recusa) {
      useAlert(motivoDaRecusa(recusa, t));
      onOpenCard(payload.cardId);
      return;
    }
    useAlert(t('KANBAN.MOVE_FAILED'));
    await kanban.fetchBoard();
  }
};

const onReorderStages = async stages => {
  try {
    await kanban.reorderStages(stages);
  } catch (error) {
    useAlert(t('KANBAN.STAGE_SAVE_FAILED'));
  }
};

const fieldManagerRef = ref(null);
const cardCreateDialogRef = ref(null);

// CRM-01g: the inverse path. Opening the drawer on the freshly created card is the
// natural next step — the operator almost always wants to fill in the rest.
//
// Goes through onOpenCard, not straight to fetchCardDetails: the drawer renders on
// `openCardId`, so loading the details without setting it fetches data nobody sees.
const onCardCreated = card => onOpenCard(card.id);

// The duplicate warning offers the card that already exists. Close the dialog
// first — otherwise the drawer opens behind it and the click looks broken.
const onOpenExistingCard = cardId => {
  cardCreateDialogRef.value?.close();
  onOpenCard(cardId);
};

const pipelineDialogRef = ref(null);

// /kanban/new is a URL, not a mode: once the dialog is done with (saved or dismissed) put
// the user back on a board so the address bar never sits on a route with nothing behind it.
const leaveNewPipelineRoute = () => {
  if (!props.openNewPipeline) return;
  const target = kanban.activePipelineId;
  router.replace(
    target
      ? { name: 'kanban_pipeline', params: { pipelineId: target } }
      : { name: 'kanban_index' }
  );
};

const onPipelineSubmit = async ({ id, payload }) => {
  try {
    if (id) {
      await kanban.updatePipeline(id, payload);
    } else {
      const created = await kanban.createPipeline(payload);
      // Land on the new board so the operator sees what they just made — unfiltered, since
      // the filters of the board they came from mean nothing on an empty one.
      kanban.loadFilters({});
      await kanban.setActivePipeline(created.id);
      router.push({
        name: 'kanban_pipeline',
        params: { pipelineId: created.id },
      });
    }
    // The sidebar keeps its own copy of the pipeline list; tell it to refetch rather than
    // wiring it to this store.
    emitter.emit(BUS_EVENTS.KANBAN_PIPELINES_CHANGED);
    useAlert(t('KANBAN.PIPELINE.SAVED'));
  } catch (error) {
    useAlert(t('KANBAN.PIPELINE.SAVE_FAILED'));
  }
};

const onPipelineDelete = async id => {
  try {
    await kanban.deletePipeline(id);
    emitter.emit(BUS_EVENTS.KANBAN_PIPELINES_CHANGED);
    useAlert(t('KANBAN.PIPELINE.DELETED'));
  } catch (error) {
    useAlert(t('KANBAN.PIPELINE.SAVE_FAILED'));
  }
};

const onStageSubmit = async ({ id, payload }) => {
  try {
    if (id) await kanban.updateStage(id, payload);
    else await kanban.createStage(payload);
    useAlert(t('KANBAN.STAGE_SAVED'));
  } catch (error) {
    useAlert(t('KANBAN.STAGE_SAVE_FAILED'));
  }
};

const onStageDelete = async ({ id, moveToStageId }) => {
  try {
    await kanban.deleteStage(id, moveToStageId);
    useAlert(t('KANBAN.STAGE_DELETED'));
  } catch (error) {
    useAlert(t('KANBAN.STAGE_SAVE_FAILED'));
  }
};

const onRefresh = () => kanban.fetchBoard();

// CRM-01h fase 3: "Carregar mais" de uma coluna.
const onLoadMore = async stageId => {
  try {
    await kanban.loadMore(stageId);
  } catch (error) {
    useAlert(t('KANBAN.COLUMN.LOAD_MORE_FAILED'));
  }
};

// CRM-01h fase 3: "mover em bloco". O servidor move em fatias; a tela consulta o lote a cada 2 s
// e, no fim, recarrega o quadro uma vez e dá o recado.
const bulkDialogRef = ref(null);
let desmontado = false;

const esperar = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const acompanharLote = async () => {
  while (!desmontado && kanban.bulkMove?.status === 'running') {
    // eslint-disable-next-line no-await-in-loop
    await esperar(2000);
    try {
      // eslint-disable-next-line no-await-in-loop
      await kanban.refreshBulkMove();
    } catch (error) {
      // Uma consulta que cai não perde o lote: ele segue no servidor e a próxima consulta o acha.
    }
  }
  if (desmontado) return;
  const lote = kanban.bulkMove;
  kanban.clearSelection();
  await kanban.fetchBoard();
  const contagem = { moved: lote?.moved, errors: lote?.errors };
  if (lote?.status !== 'finished') useAlert(t('KANBAN.BULK.FAILED'));
  else {
    const resumo = lote.errors
      ? t('KANBAN.BULK.DONE_WITH_ERRORS', contagem, lote.moved)
      : t('KANBAN.BULK.DONE', contagem, lote.moved);
    // RN-02: o lote pula o card cujo contato já tem outro negócio aberto no funil, e diz quantos.
    const recusados = lote.refusals?.contact_has_open_deal || 0;
    const motivo = recusados
      ? ` ${t('KANBAN.BULK.REFUSED_OPEN_DEAL', { refused: recusados }, recusados)}`
      : '';
    useAlert(`${resumo}${motivo}`);
  }
  kanban.bulkMove = null;
};

// O servidor diz por que recusou; cada recusa tem a sua frase.
const recadoDaRecusa = corpo => {
  if (corpo.error === 'bulk_move_running') return t('KANBAN.BULK.RUNNING');
  if (corpo.error === 'too_many_cards')
    return t('KANBAN.BULK.TOO_MANY', {
      count: corpo.count,
      limit: corpo.limit,
    });
  if (corpo.error === 'nothing_to_move') return t('KANBAN.BULK.NOTHING');
  if (corpo.error === 'apply_rules_admin_only')
    return t('KANBAN.BULK.RULES_ADMIN_ONLY');
  return t('KANBAN.BULK.FAILED');
};

const onMoveTo = stage =>
  bulkDialogRef.value?.open({ stage, count: kanban.selectedCount });

const onConfirmBulk = async ({ stage, applyRules }) => {
  try {
    await kanban.startBulkMove({ toStageId: stage.id, applyRules });
    acompanharLote();
  } catch (error) {
    useAlert(recadoDaRecusa(error?.response?.data || {}));
  }
};

// Um lote terminou (de outra tela, ou de outra pessoa): recarrega o quadro uma vez. O próprio
// lote desta tela recarrega no fim do acompanhamento, então não recarrega duas vezes.
const onBoardChanged = data => {
  if (Number(data?.pipeline_id) !== kanban.activePipelineId) return;
  if (kanban.bulkMove && data?.bulk_move_id === kanban.bulkMove.id) return;
  kanban.fetchBoard();
};

// Another agent moved a card — patch our board instead of refetching it.
const onRemoteMove = data => kanban.applyRemoteMove(data);

// CRM-04 R2-T7: a face do negócio mudou (o "aguardando" a cada mensagem, os contatos, as conversas). O
// painel aberto relê quando a lista dele ficou velha.
const onRemoteUpdate = async data => {
  try {
    await kanban.receiveCardUpdate(data);
  } catch (error) {
    useAlert(t('KANBAN.DRAWER.LOAD_FAILED'));
  }
};

// Dois negócios viraram um: o absorvido sai do quadro e, se era ele no painel, o painel abre o que ficou.
const onRemoteMerge = data => {
  if (kanban.applyMerge(data)) onOpenCard(data.survivor_id);
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  store.dispatch('labels/get');

  await kanban.fetchPipelines();
  if (props.pipelineId) kanban.activePipelineId = Number(props.pipelineId);
  kanban.loadFilters(filtrosIniciais(kanban.activePipelineId));
  await kanban.fetchBoard();
  sincronizarUrl();

  emitter.on(BUS_EVENTS.KANBAN_CARD_MOVED, onRemoteMove);
  emitter.on(BUS_EVENTS.KANBAN_BOARD_CHANGED, onBoardChanged);
  emitter.on(BUS_EVENTS.KANBAN_CARD_UPDATED, onRemoteUpdate);
  emitter.on(BUS_EVENTS.KANBAN_CARD_MERGED, onRemoteMerge);

  if (props.openNewPipeline) pipelineDialogRef.value?.open(null);
});

onUnmounted(() => {
  desmontado = true;
  emitter.off(BUS_EVENTS.KANBAN_CARD_MOVED, onRemoteMove);
  emitter.off(BUS_EVENTS.KANBAN_BOARD_CHANGED, onBoardChanged);
  emitter.off(BUS_EVENTS.KANBAN_CARD_UPDATED, onRemoteUpdate);
  emitter.off(BUS_EVENTS.KANBAN_CARD_MERGED, onRemoteMerge);
});

watch(
  () => props.pipelineId,
  async value => {
    if (!value || Number(value) === kanban.activePipelineId) return;
    const id = Number(value);
    kanban.loadFilters(filtrosIniciais(id));
    await kanban.setActivePipeline(id);
  }
);

// Navigating from a board straight to /kanban/new keeps this component mounted, so
// onMounted does not fire again.
watch(
  () => props.openNewPipeline,
  value => {
    if (value) pipelineDialogRef.value?.open(null);
  }
);
</script>

<template>
  <div class="flex flex-col w-full h-full min-w-0 my-3 ltr:ml-3 rtl:mr-3">
    <!-- CRM-01h: duas linhas, como no HubSpot. Em cima, o pipeline (título do quadro) e as
         ações; embaixo, a busca e uma pílula por filtro. -->
    <header
      class="flex flex-col gap-2 px-4 py-3 border rounded-t-pz bg-n-card border-n-weak"
    >
      <div class="flex items-center min-w-0 gap-1.5">
        <KanbanPipelineSwitcher
          :pipelines="kanban.pipelines"
          :active-id="kanban.activePipelineId"
          @select="trocarPipeline"
        />
        <button
          v-if="canManage"
          type="button"
          class="grid flex-shrink-0 rounded-lg size-8 place-content-center text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
          :title="$t('KANBAN.PIPELINE.EDIT_TITLE')"
          :aria-label="$t('KANBAN.PIPELINE.EDIT_TITLE')"
          @click="pipelineDialogRef?.open(pipeline)"
        >
          <Icon icon="i-lucide-settings" class="size-4" />
        </button>
        <div class="flex-1" />
        <button
          type="button"
          :disabled="kanban.uiFlags.fetchingBoard"
          class="grid flex-shrink-0 rounded-lg size-8 place-content-center text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-50"
          :title="$t('KANBAN.REFRESH')"
          :aria-label="$t('KANBAN.REFRESH')"
          @click="onRefresh"
        >
          <Icon
            icon="i-lucide-refresh-cw"
            class="size-4"
            :class="{ 'animate-spin': kanban.uiFlags.fetchingBoard }"
          />
        </button>
        <Button
          :label="$t('KANBAN.CREATE_CARD.BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          class="flex-shrink-0"
          @click="cardCreateDialogRef?.open()"
        />
      </div>

      <KanbanFilters v-model="filters" @reset="kanban.resetFilters()" />
    </header>

    <KanbanBulkBar
      v-if="kanban.hasSelection || kanban.bulkMove"
      :count="kanban.selectedCount"
      :stages="kanban.stages"
      :progress="kanban.bulkMove"
      @move-to="onMoveTo"
      @clear="kanban.clearSelection()"
    />

    <div
      class="flex flex-1 min-h-0 border border-t-0 rounded-b-pz bg-n-card border-n-weak"
    >
      <p
        v-if="!kanban.pipelines.length && !kanban.uiFlags.fetchingPipelines"
        class="p-8 text-sm text-center text-n-slate-11"
      >
        {{ $t('KANBAN.EMPTY') }}
      </p>
      <KanbanBoard
        v-else
        :stages="kanban.stages"
        :columns="kanban.columns"
        :is-loading="kanban.uiFlags.fetchingBoard"
        :show-value="showValue"
        :currency="currency"
        :can-manage="canManage"
        :selection-active="kanban.hasSelection"
        :is-card-selected="kanban.isCardSelected"
        :is-stage-selected="kanban.isStageSelected"
        @drop="onDrop"
        @edit-stage="onEditStage"
        @add-stage="onAddStage"
        @open-card="onOpenCard"
        @reorder-stages="onReorderStages"
        @toggle-card="kanban.toggleCard"
        @toggle-stage="kanban.toggleStage"
        @load-more="onLoadMore"
      />
      <KanbanCardDrawer
        v-if="openCardId"
        :card-id="openCardId"
        :currency="currency"
        :show-value="showValue"
        :can-manage="canManage"
        @close="onCloseCard"
        @manage-fields="fieldManagerRef?.open()"
        @open-card="onOpenCard"
      />
    </div>

    <StageDialog
      ref="stageDialogRef"
      @submit="onStageSubmit"
      @delete="onStageDelete"
    />
    <CardCreateDialog
      ref="cardCreateDialogRef"
      @created="onCardCreated"
      @open-card="onOpenExistingCard"
    />
    <FieldManagerDialog ref="fieldManagerRef" />
    <KanbanBulkMoveDialog
      ref="bulkDialogRef"
      :can-apply-rules="canManage"
      @confirm="onConfirmBulk"
    />
    <PipelineDialog
      ref="pipelineDialogRef"
      @submit="onPipelineSubmit"
      @delete="onPipelineDelete"
      @close="leaveNewPipelineRoute"
    />
  </div>
</template>
